import { lazy, Suspense } from "react";
import { useAuth } from "../../auth/AuthContext";
import BasicEditor from "./BasicEditor";
import Navigation from "../Shared/Navigation";
import { useT } from "../../i18n/I18nContext";

// ``WriteProject`` is lazy because it pulls in TipTap + Yjs +
// Hocuspocus + ChatAi (markdown + DOMPurify + the AI client) +
// the export / sprint / image-generator surfaces — collectively
// the heaviest component tree in the app. Eager-importing it
// here meant every visitor of /editor/* (including the anonymous
// scratchpad path below) downloaded the whole editor. The lazy
// import keeps anonymous visitors on ``BasicEditor`` only and
// pays the WriteProject chunk cost only when an authenticated
// user actually opens the gate.
const WriteProject = lazy(() => import("./WriteProject"));

// Replaces the blanket ``RequireAuth`` gate on ``/editor/*``.
//
// Signed-in writers fall through to ``WriteProject`` — the full
// collab editor with chapters, AI tools, exports, sprint timer.
//
// Anonymous visitors get a *real* editor (``BasicEditor``) instead
// of a "log in to write" wall. It's a localStorage-backed scratch
// pad so the draft survives reloads, but never touches the backend
// — no auth, no presign, no Hocuspocus handshake. The upgrade
// pitch lives at the bottom of the editor itself for visitors who
// later want to turn the draft into a real project.
export default function EditorAccess() {
  const t = useT();
  const { user, status } = useAuth();

  if (status === "loading") {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-bg text-secondText text-sm"
        role="status"
        aria-live="polite"
      >
        {t("auth.loading")}
      </div>
    );
  }

  if (user) {
    return (
      <Suspense
        fallback={
          <div
            className="min-h-screen flex items-center justify-center bg-bg text-secondText text-sm"
            role="status"
            aria-live="polite"
          >
            <span className="opacity-60">{t("auth.loading")}</span>
          </div>
        }
      >
        <WriteProject />
      </Suspense>
    );
  }

  return (
    <>
      <Navigation searchQuery="" setSearchQuery={() => {}} />
      <BasicEditor />
    </>
  );
}
